import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { SiteButton } from "@/components/brand/SiteButton";
import { Container } from "@/components/layout/Section";
import HeroVideo from "@/components/home/HeroVideo";
import { HERO } from "@/config/homeContent";
import { api } from "@/lib/api";

const lineReveal = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fade = {
  hidden: { opacity: 0, y: 14 },
  visible: (d) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: d, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Hero = () => {
  const reduced = useReducedMotion();
  const [cms, setCms] = useState(null);

  // Homepage hero copy is CMS-editable (Admin → Content → Pages → home); static config is the fallback.
  useEffect(() => {
    api.get("/pages/home").then((r) => setCms(r.data.sections || {})).catch(() => {});
  }, []);

  const eyebrow = cms?.hero_eyebrow || HERO.eyebrow;
  const lines = cms
    ? [cms.hero_line_1, cms.hero_line_2, cms.hero_line_3].filter(Boolean)
    : HERO.lines;
  const sub = cms?.hero_sub || HERO.sub;

  return (
    <section
      data-testid="hero-section"
      className="relative flex min-h-[92vh] items-center overflow-hidden pt-28 pb-16"
    >
      {/* cinematic brand aurora loop + legibility scrims */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-80">
        <HeroVideo />
      </div>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(5,6,7,0.88) 0%, rgba(5,6,7,0.55) 45%, rgba(5,6,7,0.25) 100%), linear-gradient(0deg, rgba(5,6,7,0.9) 0%, transparent 32%)",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-brand-black to-transparent"
      />

      <Container className="relative">
        {reduced ? (
          <div className="space-y-6">
            <p className="eyebrow">{eyebrow}</p>
            <h1 className="max-w-4xl font-display text-4xl font-bold leading-[1.04] tracking-tight text-brand-snow sm:text-5xl lg:text-[4.4rem]">
              {lines.map((line, i) => (
                <span key={line} className="block">
                  {i === HERO.highlightLine ? (
                    <span className="text-brand-gradient">{line}</span>
                  ) : (
                    line
                  )}
                </span>
              ))}
            </h1>
            <p className="max-w-xl text-base leading-relaxed text-brand-mist sm:text-lg">{sub}</p>
            <HeroCtas />
          </div>
        ) : (
          <div className="space-y-7">
            <motion.p variants={fade} initial="hidden" animate="visible" custom={0} className="eyebrow">
              {eyebrow}
            </motion.p>
            <h1 className="max-w-4xl font-display text-4xl font-bold leading-[1.04] tracking-tight text-brand-snow sm:text-5xl lg:text-[4.4rem]">
              {lines.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block will-change-transform"
                    variants={lineReveal}
                    initial="hidden"
                    animate="visible"
                    custom={i}
                  >
                    {i === HERO.highlightLine ? (
                      <span className="text-brand-gradient">{line}</span>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              variants={fade}
              initial="hidden"
              animate="visible"
              custom={0.75}
              className="max-w-xl text-base leading-relaxed text-brand-mist sm:text-lg"
            >
              {sub}
            </motion.p>
            <motion.div variants={fade} initial="hidden" animate="visible" custom={0.9}>
              <HeroCtas />
            </motion.div>
          </div>
        )}
      </Container>
    </section>
  );
};

const HeroCtas = () => (
  <div className="flex flex-wrap items-center gap-4" data-testid="hero-ctas">
    <SiteButton to={HERO.primaryCta.to} testid="hero-cta-discovery-call" size="lg">
      {HERO.primaryCta.label}
      <ArrowRight size={16} />
    </SiteButton>
    <SiteButton to={HERO.secondaryCta.to} variant="outline" testid="hero-cta-explore-services" size="lg">
      {HERO.secondaryCta.label}
    </SiteButton>
  </div>
);

export default Hero;
